import { useEffect, useMemo, useState } from 'react';
import Toast, { useToast } from '../../components/Toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { format, startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';

// Compute the most recent completed Sun–Sat week
const lastWeekStart = () => format(startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }), 'yyyy-MM-dd');
const lastWeekEnd   = () => format(endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 }), 'yyyy-MM-dd');

// True if [aFrom, aTo] overlaps [bFrom, bTo] (inclusive)
const rangesOverlap = (aFrom, aTo, bFrom, bTo) => aFrom <= bTo && aTo >= bFrom;

export default function PayrollRuns() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const { toast, showToast, hideToast } = useToast();

  // Sorting
  const [sortKey, setSortKey] = useState('week_start');
  const [sortAsc, setSortAsc] = useState(false);

  // New run modal
  const [showNewRun, setShowNewRun] = useState(false);
  const [newRunFrom, setNewRunFrom] = useState('');
  const [newRunTo, setNewRunTo] = useState('');
  const [creating, setCreating] = useState(false);

  // Backfill modal
  const [showBackfill, setShowBackfill] = useState(false);
  const [backfillFrom, setBackfillFrom] = useState('');
  const [backfilling, setBackfilling] = useState(false);

  // Delete run
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);


  const loadRuns = async () => {
    const { data, error } = await supabase
      .from('payroll_runs')
      .select('*')
      .order('week_start', { ascending: false });
    if (error) console.error('PayrollRuns load error:', error);
    setRuns(data || []);
    setLoading(false);
  };

  useEffect(() => { loadRuns(); }, []); // eslint-disable-line

  // ── New run modal ──────────────────────────────────────────────────────────
  const openNewRun = () => {
    setNewRunFrom(lastWeekStart());
    setNewRunTo(lastWeekEnd());
    setShowNewRun(true);
  };

  const overlappingRun = (from, to, excludeId) =>
    runs.find(r => r.id !== excludeId && rangesOverlap(from, to, r.week_start, r.week_end));

  const handleCreateRun = async () => {
    if (!newRunFrom || !newRunTo || newRunFrom > newRunTo) {
      showToast('Enter a valid date range.', 'error'); return;
    }
    const overlap = overlappingRun(newRunFrom, newRunTo);
    if (overlap) {
      const label = `${format(new Date(overlap.week_start + 'T12:00:00'), 'MMM d')}–${format(new Date(overlap.week_end + 'T12:00:00'), 'MMM d, yyyy')}`;
      if (overlap.week_start === newRunFrom && overlap.week_end === newRunTo) {
        navigate(`/finance/payroll/${overlap.id}`);
        return;
      }
      // payroll_runs.week_start is UNIQUE. "Create anyway" on a run sharing
      // the start date used to offer something the database would refuse.
      if (overlap.week_start === newRunFrom) {
        showToast(`A run already starts on ${format(new Date(newRunFrom + 'T12:00:00'), 'MMM d, yyyy')} (${label}). Open it, or edit its dates.`, 'error');
        return;
      }
      if (!window.confirm(`This range overlaps an existing run (${label}). Create anyway?`)) return;
    }
    setCreating(true);
    const { data, error } = await supabase.from('payroll_runs').insert([{
      week_start: newRunFrom,
      week_end: newRunTo,
      status: 'draft',
      created_by_id: profile?.id,
      created_by_name: profile?.full_name,
    }]).select().single();
    setCreating(false);
    if (error) { showToast('Failed to create run: ' + error.message, 'error'); return; }
    navigate(`/finance/payroll/${data.id}`);
  };

  // ── Backfill ───────────────────────────────────────────────────────────────
  // Default to the first Sunday after the latest run, or eight weeks back
  // when there are none — the date this used to hardcode went stale in May.
  const openBackfill = () => {
    const latest = runs.map(r => r.week_end).sort().pop();
    const from = latest
      ? new Date(new Date(latest + 'T12:00:00').getTime() + 86400000)
      : subWeeks(new Date(), 8);
    setBackfillFrom(format(startOfWeek(from, { weekStartsOn: 0 }), 'yyyy-MM-dd'));
    setShowBackfill(true);
  };

  const handleBackfill = async () => {
    if (!backfillFrom) return;
    setBackfilling(true);
    const weeks = [];
    let cur = startOfWeek(new Date(backfillFrom + 'T12:00:00'), { weekStartsOn: 0 });
    const lastSun = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 0 });
    while (cur <= lastSun) {
      const ws = format(cur, 'yyyy-MM-dd');
      const we = format(endOfWeek(cur, { weekStartsOn: 0 }), 'yyyy-MM-dd');
      // Skip any week an existing run touches, not only an exact start match:
      // a custom-range run covering part of a week must not get a second run
      // paying the same days.
      if (!overlappingRun(ws, we)) {
        weeks.push({ week_start: ws, week_end: we, status: 'draft', created_by_id: profile?.id, created_by_name: profile?.full_name });
      }
      cur = new Date(cur.getTime() + 7 * 86400000);
    }
    if (weeks.length === 0) { showToast('All weeks in that range already exist.'); setBackfilling(false); setShowBackfill(false); return; }
    const { error } = await supabase.from('payroll_runs').insert(weeks);
    setBackfilling(false);
    setShowBackfill(false);
    if (error) { showToast('Backfill error: ' + error.message, 'error'); return; }
    showToast(`Created ${weeks.length} payroll run${weeks.length > 1 ? 's' : ''}. Open each to pull AxisCare data.`);
    loadRuns();
  };

  // ── Delete draft run ───────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    // Draft only, enforced here as well as by the hidden button.
    const { error } = await supabase.from('payroll_runs').delete().eq('id', deleteConfirm.id).eq('status', 'draft');
    setDeleting(false);
    setDeleteConfirm(null);
    if (error) { showToast('Delete failed: ' + error.message, 'error'); return; }
    showToast('Draft run deleted.');
    loadRuns();
  };

  // ── Sorting ────────────────────────────────────────────────────────────────
  const sorting = useMemo(() => sortKey ? [{ id: sortKey, desc: !sortAsc }] : [], [sortKey, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortKey(next[0].id);
    setSortAsc(!next[0].desc);
  };

  const filtered = filter === 'All' ? runs : runs.filter(r => r.status === filter.toLowerCase());
  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortKey]; let bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1; if (bv == null) return -1;
    const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortAsc ? cmp : -cmp;
  });

  const total = runs.length;
  const drafts = runs.filter(r => r.status === 'draft').length;
  const finals = runs.filter(r => r.status === 'finalized').length;
  const lastFinal = runs.find(r => r.status === 'finalized');

  const columns = [
    {
      id: 'week_start', header: 'Week', size: 190,
      meta: { mobile: 'primary' },
      cell: info => {
        const r = info.row.original;
        return `${format(new Date(r.week_start + 'T12:00:00'), 'MMM d')}–${format(new Date(r.week_end + 'T12:00:00'), 'MMM d, yyyy')}`;
      },
    },
    {
      id: 'period', header: 'Period', size: 80, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: () => <span style={{ color: 'var(--text3)', fontSize: 12 }}>Sun–Sat</span>,
    },
    {
      accessorKey: 'staff_count', header: 'Staff', size: 80,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ color: 'var(--text2)' }}>{info.getValue() ?? '—'}</span>,
    },
    {
      accessorKey: 'total_hours', header: 'Hours', size: 100,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ color: 'var(--text2)' }}>{info.getValue() != null ? `${Number(info.getValue()).toFixed(1)}h` : '—'}</span>,
    },
    {
      accessorKey: 'total_pay', header: 'Total payroll', size: 130,
      meta: { mobile: 'primary' },
      cell: info => info.getValue() != null ? `$${Number(info.getValue()).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—',
    },
    {
      accessorKey: 'status', header: 'Status', size: 120,
      meta: { mobile: 'primary' },
      cell: info => {
        const status = info.getValue();
        return (
          <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20,
            background: status === 'finalized' ? 'var(--success-bg)' : 'var(--warn-bg)',
            color: status === 'finalized' ? 'var(--success)' : 'var(--warn)' }}>
            {status === 'finalized' ? '✓ Finalized' : '✎ Draft'}
          </span>
        );
      },
    },
    {
      accessorKey: 'created_by_name', header: 'Created by', size: 140,
      meta: { mobile: 'hidden' },
      cell: info => <span style={{ color: 'var(--text3)', fontSize: 12 }}>{info.getValue() || '—'}</span>,
    },
    {
      id: 'actions', header: '', size: 130, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const r = info.row.original;
        return (
          <div style={{ color: 'var(--accent2)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
            {r.status === 'finalized' ? 'View →' : 'Review →'}
            {r.status !== 'finalized' && (
              <button
                onClick={e => { e.stopPropagation(); setDeleteConfirm(r); }}
                title="Delete draft run"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 13, padding: 0 }}
              >
                🗑
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'Total runs', value: total, tone: 'neutral' },
          { label: 'Draft', value: drafts, tone: drafts > 0 ? 'warn' : 'neutral' },
          { label: 'Finalized', value: finals, tone: 'neutral' },
          { label: 'Last run payout', value: lastFinal ? `$${Number(lastFinal.total_pay || 0).toLocaleString()}` : '—', tone: 'neutral' },
        ].map(s => (
          <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} />
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Payroll Mgmt.</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {/* Filter pills */}
            <div style={{ display: 'flex', gap: 6 }}>
              {['All', 'Draft', 'Finalized'].map(f => (
                <button key={f} onClick={() => setFilter(f)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: filter === f ? 'var(--accent)' : 'transparent', color: filter === f ? '#fff' : 'var(--text3)', borderColor: filter === f ? 'var(--accent)' : 'var(--border)' }}>{f}</button>
              ))}
            </div>
            <button
              onClick={openBackfill}
              style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--border)', background: 'transparent', color: 'var(--text3)' }}
            >
              📥 Backfill
            </button>
            <button
              onClick={openNewRun}
              style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: 'var(--accent)', color: '#fff' }}
            >
              + New Run
            </button>
          </div>
        </div>

        <DataTable
          tableId="payroll-runs"
          columns={columns}
          data={sorted}
          getRowId={r => r.id}
          onRowClick={r => navigate(`/finance/payroll/${r.id}`)}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          loading={loading}
          emptyMessage='No payroll runs yet. Click "New Run" to create the first one.'
          maxHeight="calc(100vh - 320px)"
        />
      </div>

      {/* New Run modal */}
      {showNewRun && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowNewRun(false)}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 420, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>New payroll run</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Defaults to the last completed Sun–Sat week — adjust if this run covers a different period.</div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>From</div>
                <input type="date" value={newRunFrom} onChange={e => setNewRunFrom(e.target.value)}
                  style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>To</div>
                <input type="date" value={newRunTo} onChange={e => setNewRunTo(e.target.value)}
                  style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowNewRun(false)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={handleCreateRun} disabled={creating} style={{ flex: 2, padding: '10px 0', background: creating ? '#aaa' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: creating ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                {creating ? 'Creating…' : 'Create run'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backfill modal */}
      {showBackfill && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setShowBackfill(false)}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 420, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Backfill draft runs</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Creates a draft run for every Sun–Sat week from the date below through the last completed week. Existing weeks are skipped.</div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Start from</div>
              <input type="date" value={backfillFrom} onChange={e => setBackfillFrom(e.target.value)}
                style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '6px 8px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowBackfill(false)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={handleBackfill} disabled={backfilling} style={{ flex: 2, padding: '10px 0', background: backfilling ? '#aaa' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: backfilling ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                {backfilling ? 'Backfilling…' : 'Run backfill'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setDeleteConfirm(null)}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 400, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>Delete draft run?</div>
            <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>
              {format(new Date(deleteConfirm.week_start + 'T12:00:00'), 'MMM d')}–{format(new Date(deleteConfirm.week_end + 'T12:00:00'), 'MMM d, yyyy')} and all of its line items will be permanently removed. This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={handleDelete} disabled={deleting} style={{ flex: 2, padding: '10px 0', background: deleting ? '#aaa' : 'var(--danger)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: deleting ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                {deleting ? 'Deleting…' : 'Delete run'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast {...(toast || {})} onClose={hideToast} />
    </div>
  );
}
